import { EnumButtonName } from '@calm/evexia-analytics';
import { Close } from '@mui/icons-material';
import { VideoPlayer } from '@rhg/components/media/video-player';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Dialog,
    IconButton,
    Theme,
    ThemeProvider,
    Typography,
} from '@rhg/libs/material';
import { responsivePaddingDefault, sxAppend } from '@rhg/libs/material/sx';
import { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { VideoPlayerControls } from '../video-player/types';
import { VideoPlayerDialogProps } from './types';

const styleCloseButtonContainer = {
    zIndex: (theme: Theme) => theme.zIndex.modal,
    position: 'absolute',
    top: 0,
    left: 0,
    p: (theme: Theme) => theme.ds.spacingUnits.SIDE_SMALL,
};
const styleCloseButtonContainerFullScreen = {
    p: responsivePaddingDefault,
};
const styleCloseButton = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) => theme.palette.mask.black['60p'],
    color: 'action.active',
};
const styleCloseButtonFullScreen = {
    color: 'primary.main',
};
const styleDialogBackground = {
    backgroundColor: 'black',
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
};

const stylePlayerContainer = {
    position: 'relative',
    width: '100%',
    height: '100dvh',
};

export function VideoPlayerDialog({
    videos,
    isOpen,
    title,
    subtitle,
    isCloseButtonDisplayed = true,
    showPoster = true,
    hasMinimalControls = false,
    isInitiallyMuted = false,
    previousProgress = 0,
    autoplay,
    loop,
    onClose,
    forcedOrientation,
    onProgress,
    trackDialog,
    trackProps,
    actionButton,
    onEnd,
    onControlClick,
}: VideoPlayerDialogProps) {
    const { formatMessage } = useIntl();
    const id = useId();
    const theme = useProvidedTheme('dark');

    const progressTracker = useCallback<MediaTrackerFn>(
        (progress) => {
            onProgress?.(progress);
        },
        [onProgress]
    );

    return (
        <ThemeProvider theme={theme}>
            <Dialog
                aria-labelledby={id}
                onClose={onClose}
                open={isOpen}
                trackDialog={trackDialog ?? 'VideoPlayer'}
                {...{ name: trackDialog ?? 'VideoPlayer' }}
                trackProps={trackProps}
                fullScreen
            >
                <Box sx={styleDialogBackground}>
                    <Typography id={id} variant="srOnly">
                        {formatMessage(commonMessages['common.video_player'])}
                    </Typography>
                    <Box sx={stylePlayerContainer}>
                        {isCloseButtonDisplayed && (
                            <Box
                                sx={sxAppend(
                                    styleCloseButtonContainer,
                                    styleCloseButtonContainerFullScreen
                                )}
                            >
                                <IconButton
                                    sx={sxAppend(
                                        styleCloseButton,
                                        styleCloseButtonFullScreen
                                    )}
                                    aria-label={formatMessage(
                                        commonMessages['common.dismiss_dialog']
                                    )}
                                    onClick={onClose}
                                    size="medium"
                                    trackButton={EnumButtonName.Dismiss}
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                        )}
                        {videos.length && (
                            <VideoPlayer
                                muted={isInitiallyMuted}
                                controls={
                                    hasMinimalControls
                                        ? VideoPlayerControls.Minimal
                                        : VideoPlayerControls.Full
                                }
                                loop={loop}
                                title={title}
                                subtitle={subtitle}
                                autoplay={autoplay}
                                forcedOrientation={forcedOrientation}
                                videos={videos}
                                onEnd={onEnd}
                                actionButton={actionButton}
                                previousProgress={previousProgress}
                                trackFn={progressTracker}
                                showPoster={showPoster}
                                willTrackProgress
                                onControlClick={onControlClick}
                            />
                        )}
                    </Box>
                </Box>
            </Dialog>
        </ThemeProvider>
    );
}
